/**
 * Notifications page — full-width tabbed shell (Inventory-App chrome, DESIGN.md §3).
 *
 * One sticky `PageActionBar` owns the entire top row so every control shares a
 * single baseline: the Inbox/Settings tabs are anchored LEFT as a segmented
 * control, followed (on the Inbox tab) by the unread/all filter + count; the
 * bulk actions (Mark all read, Clear all) sit RIGHT. Nothing floats.
 *
 * - **Inbox** (default): the in-app notification feed (mentions, assignments,
 *   record updates, push-blocked alerts, system messages). Full-width,
 *   day-grouped rows in one bordered container. See `NotificationInbox`.
 * - **Settings**: the org-wide Slack digest / channel configuration.
 *
 * The inbox data hooks live here (the composition root) so the action bar and
 * the list read one shared source of truth; `NotificationInbox` is the
 * presentational list. The route stays `/notifications`; `?tab=settings`
 * deep-links the config tab.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCheck, Loader2, Trash2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { useRole } from '@/hooks/use-role';
import { PageActionBar, PageActionBarDivider } from '@/components/layout/PageActionBar';
import { BackToTopPill } from '@/components/BackToTopPill';
import { cn } from '@/lib/utils';
import { NotificationInbox } from '@/features/notifications/NotificationInbox';
import { NotificationSettings } from '@/features/notifications/NotificationSettings';
import {
  useNotificationActions,
  useNotificationInbox,
  useUnreadCount,
} from '@/features/notifications/useNotifications';
import type { NotificationFilter } from '@/features/notifications/notifications-procedures';

type NotificationsTab = 'inbox' | 'settings';

// Shared segmented-control geometry — the Inbox/Settings tabs and the
// unread/all filter render as identical sibling pills on the action bar.
const SEGMENT_GROUP =
  'inline-flex h-8 items-center rounded-md border bg-muted/40 p-0.5';
const SEGMENT_BTN =
  'h-7 rounded-[5px] px-3 text-xs font-medium transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const SEGMENT_ACTIVE = 'bg-background text-foreground shadow-sm';
const SEGMENT_IDLE = 'text-muted-foreground hover:text-foreground';

export default function NotificationsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const [confirmOpen, setConfirmOpen] = useState(false);

  usePageTitle(
    t('notifications.title', { defaultValue: 'Notifications' }),
    t('notifications.page.subtitle', {
      defaultValue: 'Your mentions, assignments, and system alerts',
    }),
  );

  // DTF Printer gets the inbox only: NotificationSettings calls procedures that
  // role is refused, so its tab is never offered and ?tab=settings is ignored.
  const { isDtfPrinter } = useRole();
  const settingsAllowed = !isDtfPrinter();
  const tab: NotificationsTab =
    settingsAllowed && searchParams.get('tab') === 'settings' ? 'settings' : 'inbox';

  const setTab = (next: string) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'settings') params.set('tab', 'settings');
        else params.delete('tab');
        return params;
      },
      { replace: true },
    );
  };

  const unreadQuery = useUnreadCount();
  const inbox = useNotificationInbox(filter);
  const actions = useNotificationActions();

  const unread = unreadQuery.data ?? 0;
  const { markAllRead, dismissAll } = actions;
  const isInbox = tab === 'inbox';

  return (
    <Tabs value={tab} onValueChange={setTab} className="w-full">
      {/* Padded scroll wrapper — provides the gutter the sticky bar's -mx bleed
          aligns to (§3.2), and the top-level target for the skip link (§16). */}
      <div id="main" className="w-full px-4 pb-16 pt-6 sm:px-6 lg:px-8">
        <PageActionBar
          ariaLabel={t('notifications.title', { defaultValue: 'Notifications' })}
          left={
            <>
              <TabsList className={SEGMENT_GROUP}>
                <TabsTrigger
                  value="inbox"
                  className={cn(SEGMENT_BTN, SEGMENT_IDLE, 'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm')}
                >
                  {t('notifications.tabInbox', { defaultValue: 'Inbox' })}
                </TabsTrigger>
                {settingsAllowed && (
                  <TabsTrigger
                    value="settings"
                    className={cn(SEGMENT_BTN, SEGMENT_IDLE, 'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm')}
                  >
                    {t('notifications.tabSettings', { defaultValue: 'Settings' })}
                  </TabsTrigger>
                )}
              </TabsList>

              {isInbox && (
                <>
                  <PageActionBarDivider />
                  <div
                    className={SEGMENT_GROUP}
                    role="group"
                    aria-label={t('notifications.filter.label', {
                      defaultValue: 'Filter notifications',
                    })}
                  >
                    {(['unread', 'all'] as const).map((f) => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => setFilter(f)}
                        aria-pressed={filter === f}
                        className={cn(SEGMENT_BTN, filter === f ? SEGMENT_ACTIVE : SEGMENT_IDLE)}
                      >
                        {f === 'unread'
                          ? t('notifications.filter.unread', { defaultValue: 'Unread' })
                          : t('notifications.filter.all', { defaultValue: 'All' })}
                      </button>
                    ))}
                  </div>
                  <span className="self-center text-xs text-muted-foreground tabular-nums">
                    {t('notifications.unreadCount', {
                      count: unread,
                      defaultValue: '{{count}} unread',
                    })}
                  </span>
                </>
              )}
            </>
          }
          right={
            isInbox ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 px-3 text-xs"
                  disabled={unread === 0 || markAllRead.isPending}
                  onClick={() => markAllRead.mutate()}
                >
                  {markAllRead.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <CheckCheck className="h-3.5 w-3.5" />
                  )}
                  {t('notifications.markAllRead', { defaultValue: 'Mark all read' })}
                </Button>

                <Popover open={confirmOpen} onOpenChange={setConfirmOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1.5 px-3 text-xs text-muted-foreground transition-colors hover:border-destructive/40 hover:bg-destructive/5 hover:text-destructive"
                      disabled={inbox.items.length === 0 || dismissAll.isPending}
                    >
                      {dismissAll.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                      {t('notifications.clearAll', { defaultValue: 'Clear all' })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-72">
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <p className="text-sm font-medium">
                          {t('notifications.clearAll.confirmTitle', {
                            defaultValue: 'Clear all notifications?',
                          })}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('notifications.clearAll.confirmBody', {
                            defaultValue:
                              "This dismisses every notification in your inbox. You can't undo it.",
                          })}
                        </p>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-3 text-xs"
                          onClick={() => setConfirmOpen(false)}
                        >
                          {t('notifications.clearAll.cancel', { defaultValue: 'Cancel' })}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="h-8 gap-1.5 px-3 text-xs"
                          onClick={() => {
                            dismissAll.mutate();
                            setConfirmOpen(false);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {t('notifications.clearAll.action', { defaultValue: 'Clear all' })}
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </>
            ) : null
          }
        />

        {/* focus:outline reset avoids a stray ring when a tab panel
            programmatically focuses; mt-4 clears the action bar. */}
        <TabsContent value="inbox" className="mt-4 focus-visible:outline-none">
          <NotificationInbox filter={filter} onFilterChange={setFilter} inbox={inbox} actions={actions} />
        </TabsContent>
        {settingsAllowed && (
          <TabsContent value="settings" className="mt-4 focus-visible:outline-none">
            <NotificationSettings />
          </TabsContent>
        )}
      </div>

      <BackToTopPill />
    </Tabs>
  );
}
